/*
 * Sundial deferred script loader
 * ------------------------------
 * Keeps cart-drawer.js and quick-add.js off the initial-load parse/compile
 * budget of every route. Each heavier script is injected on the first sign of
 * real user intent, and a capture-phase safety net guarantees the relevant
 * custom element has upgraded before the destructive action runs, so neither
 * add-to-cart nor quick-add can break if the user acts faster than the network.
 *
 * Asset URLs are handed in from Liquid via window.SundialDeferredAssets
 * (cartDrawer / quickAdd) so this file stays a static, cacheable asset. URLs
 * are read at trigger time, which keeps it correct when sections are added in
 * the theme editor after first paint.
 */
(function () {
  var injected = {};

  // Inject a script tag once and return its load Promise. The custom element is
  // defined (and any existing matching element upgraded) synchronously when the
  // script executes, i.e. before the Promise resolves, so safety nets can wait
  // on this before retrying the action they intercepted.
  function injectOnce(src) {
    if (injected[src]) return injected[src];
    injected[src] = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = src;
      script.async = false; // preserve execution order vs any other injected deps
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });
    return injected[src];
  }

  function assets() {
    return window.SundialDeferredAssets || {};
  }

  // One-shot intent gate. The first time any of pointerover / pointerdown /
  // focusin lands on an element matching `selector`, fetch the asset named by
  // `key`. All three events fire BEFORE the click/submit that actually needs the
  // upgraded element, so on a normal interaction the script is already in flight
  // (or done) by the time it is used. focusin covers keyboard tab-through. We do
  // not gate on click itself: for add-to-cart a click is too late (see the
  // safety nets below), and pointer/focus events always precede a real click.
  function intentGate(selector, key) {
    var events = ['pointerover', 'pointerdown', 'focusin'];
    function handler(evt) {
      if (!evt.target || !evt.target.closest || !evt.target.closest(selector)) return;
      var src = assets()[key];
      if (!src) return; // feature not enabled on this page
      injectOnce(src);
      events.forEach(function (type) {
        document.removeEventListener(type, handler, true);
      });
    }
    events.forEach(function (type) {
      document.addEventListener(type, handler, true);
    });
  }

  /* ---- cart-drawer.js ----------------------------------------------------- */

  // Perf path: load on first hover/focus/press of the header cart link or any
  // add-to-cart control ([name="add"] covers both product-form and quick-add
  // submit buttons).
  intentGate('#cart-icon-bubble, [name="add"]', 'cartDrawer');

  // Correctness floor: product-form.js calls cart.getSectionsToRender() and
  // setActiveElement() synchronously at submit time, so <cart-drawer> must be
  // defined by then. If a fast user (cold cache, instant tap) submits before the
  // intent-loaded script arrives, hold the submit in the capture phase - before
  // product-form's own bubble-phase handler - load cart-drawer.js, then re-submit
  // so product-form runs normally against the now-upgraded element.
  document.addEventListener(
    'submit',
    function (evt) {
      if (customElements.get('cart-drawer')) return; // upgraded - normal path
      var src = assets().cartDrawer;
      if (!src) return; // notification mode etc.
      var form = evt.target;
      if (!form || !form.closest || !form.closest('product-form')) return;
      evt.preventDefault();
      evt.stopImmediatePropagation();
      var submitter = evt.submitter;
      injectOnce(src)
        .then(function () {
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit(submitter || undefined);
          } else {
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        })
        .catch(function () {
          form.submit(); // CDN failure: fall back to a native /cart/add post
        });
    },
    true
  );

  /* ---- quick-add.js ------------------------------------------------------- */

  // Perf path: load on first hover/focus/press of a standard quick-add ("Choose
  // options") opener. Bulk quick-add (#QuickAddBulk-) uses modal-dialog from
  // global.js and needs no extra script; the trailing hyphen excludes it.
  intentGate('modal-opener[data-modal^="#QuickAdd-"]', 'quickAdd');

  // Correctness floor: modal-opener (global.js) calls modal.show() synchronously
  // on click, and show() only exists once quick-add.js has upgraded
  // <quick-add-modal>. If a fast user clicks before the intent-loaded script
  // arrives, hold the click in the capture phase - before modal-opener's handler
  // - load quick-add.js, then open the modal ourselves.
  document.addEventListener(
    'click',
    function (evt) {
      if (customElements.get('quick-add-modal')) return; // upgraded - normal path
      var src = assets().quickAdd;
      if (!src) return;
      var opener =
        evt.target && evt.target.closest && evt.target.closest('modal-opener[data-modal^="#QuickAdd-"]');
      if (!opener) return;
      evt.preventDefault();
      evt.stopImmediatePropagation();
      var button = opener.querySelector('button');
      injectOnce(src)
        .then(function () {
          var modal = document.querySelector(opener.getAttribute('data-modal'));
          if (modal && typeof modal.show === 'function') modal.show(button);
        })
        .catch(function () {});
    },
    true
  );
})();
